/**
 * Throttle state for the post-download conversion nudge.
 *
 * Research-backed: nudge at the *value moment* (just got an asset) but don't
 * nag — so we only prompt once the user has shown intent (≥2 free downloads)
 * and at most once per day. All localStorage, best-effort.
 */

const NUDGE_DATE_KEY = "uefn-conversion-nudge-date";
const DL_COUNT_KEY = "uefn-free-download-count";

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Increment the lifetime free-download counter; returns the new total. */
export function recordFreeDownload(): number {
  if (typeof window === "undefined") return 0;
  try {
    const n = parseInt(localStorage.getItem(DL_COUNT_KEY) || "0", 10) + 1;
    localStorage.setItem(DL_COUNT_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

/**
 * Show the post-download conversion nudge at most once per day, and only once
 * the user has shown intent (≥2 free downloads total).
 */
export function shouldShowConversionNudge(totalFreeDownloads: number): boolean {
  if (typeof window === "undefined") return false;
  if (totalFreeDownloads < 2) return false;
  try {
    return localStorage.getItem(NUDGE_DATE_KEY) !== today();
  } catch {
    return false;
  }
}

export function markConversionNudgeShown(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(NUDGE_DATE_KEY, today());
  } catch {
    /* ignore */
  }
}

// ─── Soft-wall interstitial ──────────────────────────────────────────────────

const SOFT_WALL_DATE_KEY = "uefn-soft-wall-date";
const DAILY_DL_KEY = "uefn-free-downloads-today";

/** Free downloads on the current UTC day before the soft wall appears. */
const SOFT_WALL_THRESHOLD = 3;

/** Increment today's free-download counter (resets each day); returns today's total. */
export function recordDailyFreeDownload(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = localStorage.getItem(DAILY_DL_KEY);
    const parsed = raw ? (JSON.parse(raw) as { date?: string; count?: number }) : null;
    const count = parsed?.date === today() ? (parsed.count ?? 0) + 1 : 1;
    localStorage.setItem(DAILY_DL_KEY, JSON.stringify({ date: today(), count }));
    return count;
  } catch {
    return 0;
  }
}

/**
 * The mid-session trial interstitial: after the user's Nth free download of
 * the day they're clearly getting value — pitch the unlimited trial once, at
 * most once per day, fully dismissible.
 */
export function shouldShowSoftWall(todayCount: number): boolean {
  if (typeof window === "undefined") return false;
  if (todayCount < SOFT_WALL_THRESHOLD) return false;
  try {
    return localStorage.getItem(SOFT_WALL_DATE_KEY) !== today();
  } catch {
    return false;
  }
}

export function markSoftWallShown(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SOFT_WALL_DATE_KEY, today());
  } catch {
    /* ignore */
  }
}

const CREATOR_NUDGE_KEY = "uefn-creator-nudge-shown";

/**
 * One-time "become a creator" pitch for engaged free-downloaders. Someone who
 * has grabbed ≥4 free assets is clearly an active UEFN dev — exactly the cohort
 * that should be on the supply side. Shown once, ever (the dashboard card and
 * /sell carry the message persistently after that).
 */
export function shouldShowCreatorNudge(totalFreeDownloads: number): boolean {
  if (typeof window === "undefined") return false;
  if (totalFreeDownloads < 4) return false;
  try {
    return localStorage.getItem(CREATOR_NUDGE_KEY) !== "1";
  } catch {
    return false;
  }
}

export function markCreatorNudgeShown(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CREATOR_NUDGE_KEY, "1");
  } catch {
    /* ignore */
  }
}
